import { FC, useCallback, useEffect, useState } from "react";
import { format } from "date-fns";
import {
  DEFAULT_ITEMS_PER_PAGE,
  DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY,
  DEFAULT_SEARCH_INPUT_LENGTH,
} from "../../../constants/app";
import useDebouncedValue from "../../../hooks/useDebouncedValue";
import { usePrevious } from "../../../hooks/usePrevious";
import Roles from "../../../models/roles";
import { AUTH_ROUTE } from "../../../pages/App/routes";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../store";
import {
  hideTransparentPreloaderAction,
  showTransparentPreloaderAction,
} from "../../../store/application/slice";
import { getPendingTimesheetThunk } from "../../../store/timesheet/thunks";
import { ITimesheetData, TTimesheetSort } from "../../../store/timesheet/types";
import { SortOrder } from "../../../store/vacancy/types";
import PaginationControls from "../../Pagination/PaginationControls";
import PopupModal from "../../UI/Popup";
import SearchAndFilter from "./common/SearchAndFilter";
import TableHeader from "./common/TableHeader";
import TableRow from "./common/TableRow";
import TabsHeader from "./common/TabsHeader";
import ViewTimesheet from "./common/ViewTimesheet";
import TimeSheetAlert from "./TimeSheetAlert";

const TimesheetPending: FC = () => {
  const dispatch = useDispatch();

  const user = useSelector((state) => state.auth.user);

  const isClient =
    (user?.role?.name as Roles) === Roles.CENTER_ADMIN ||
    (user?.role?.name as Roles) === Roles.CENTER_USER;
  const { inProgress, pendingTimesheets, pendingPagination, totalOverDueTimesheetCount, totalPendingTimesheetCount } = useSelector(
    (state) => state.timesheet
  );
  const isAdmin =
    (user?.role?.name as Roles) === Roles.SUPER_ADMIN ||
    (user?.role?.name as Roles) === Roles.GENERAL_ADMIN;

  const [selectedTimesheet, setSelectedTimesheet] =
    useState<ITimesheetData | null>(null);
  const [showViewTimesheetModal, setShowViewTimesheetModal] =
    useState<boolean>(false);
  // Local states for sorting
  const [sortBy, setSortBy] = useState<TTimesheetSort>(isAdmin ? "timesheet_status" : "first_name");
  const [sortOrder, setSortOrder] = useState<SortOrder>(isAdmin ? "desc" : "asc");

  // Search and debouncing states
  const [keyword, setKeyword] = useState("");
  const [selectedCentreIds, setSelectedCentreIds] = useState<number[]>([]);
  const [selectedCandidateIds, setSelectedCandidateIds] = useState<number[]>(
    []
  );
  const [selectedDateRange, setSelectedDateRange] = useState<
    [Date | null, Date | null] | null
  >(null);

  const [searchInput, setSearchInput] = useState<string>("");
  const debouncedSearch = useDebouncedValue(
    searchInput,
    DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY
  );

  // Pagination states
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(DEFAULT_ITEMS_PER_PAGE);
  const currentParams = {
    keyword,
    selectedCentreIds,
    selectedCandidateIds,
    selectedDateRange,
    page,
    perPage,
    sortBy,
    sortOrder,
  };
  const previousParams = usePrevious(currentParams);

  // Handle sorting logic
  const handleSortChange = (field: TTimesheetSort) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  // Handle pagination link click
  const handlePageChange = (url: string | null) => {
    if (url) {
      const page = new URL(url).searchParams.get("page");
      setPage(Number(page));
    }
  };

  const handleViewTimesheet = (timesheet: ITimesheetData) => {
    setSelectedTimesheet(timesheet);
    setShowViewTimesheetModal(true);
  };

  const onClickClose = () => {
    setSelectedTimesheet(null);
    setShowViewTimesheetModal(false);
  };

  const fetchPendingTImesheets = useCallback(async () => {
    const prev = previousParams;

    const areSame =
      prev &&
      prev.keyword === currentParams.keyword &&
      prev.page === currentParams.page &&
      prev.perPage === currentParams.perPage &&
      prev.sortBy === currentParams.sortBy &&
      prev.sortOrder === currentParams.sortOrder &&
      prev.selectedCentreIds === currentParams.selectedCentreIds &&
      prev.selectedCandidateIds === currentParams.selectedCandidateIds &&
      prev.selectedDateRange === currentParams.selectedDateRange;
    if (areSame) {
      return; // Skip if no actual change
    }

    dispatch(showTransparentPreloaderAction());

    try {
      // Build the API parameters
      const apiParams = {
        searchValue: currentParams.keyword,
        page: currentParams.page,
        per_page: currentParams.perPage,
        sort_by: currentParams.sortBy,
        sort_order: currentParams.sortOrder,
        centres: currentParams.selectedCentreIds,
        candidates: currentParams.selectedCandidateIds,
        ...(currentParams.selectedDateRange && {
          start_date: format(currentParams.selectedDateRange[0]!, "yyyy-MM-dd"),
          end_date: format(currentParams.selectedDateRange[1]!, "yyyy-MM-dd"),
        }),
      };

      // Now, make the timesheet request
      await dispatch(getPendingTimesheetThunk(apiParams));
    } finally {
      dispatch(hideTransparentPreloaderAction());
    }
  }, [dispatch, inProgress, currentParams, previousParams]);

  useEffect(() => {
    fetchPendingTImesheets();
  }, [
    fetchPendingTImesheets,
    keyword,
    selectedCandidateIds,
    selectedCentreIds,
    selectedDateRange,
    page,
    perPage,
    sortBy,
    sortOrder,
  ]);

  // Update debounced keyword and reset page when typing stops
  useEffect(() => {
    if (
      debouncedSearch.length === 0 ||
      debouncedSearch.length > DEFAULT_SEARCH_INPUT_LENGTH
    ) {
      setKeyword(debouncedSearch);
      setPage(1); // reset to first page on new search
    }
  }, [debouncedSearch]);



  return (
    <div className="d-flex flex-column gap-20">
      <TimeSheetAlert
        totalPending={totalPendingTimesheetCount || 0}
        totalOverdue={totalOverDueTimesheetCount}
        goToLink={AUTH_ROUTE.ADMIN_TIMESHEET_PAYROLL_PENDING}
      />
      <div className="d-flex flex-column gap-10">
        <div className="d-flex flex-column">
          <TabsHeader
            role={isClient ? "centre_admin" : "super_admin"}
            activeTab="pending"
          />
          <div className="bg-black w-100 h-5px"></div>
        </div>
        <SearchAndFilter
          variant={isClient ? "client-pending" : "admin-pending"}
          keyword={searchInput}
          selectedCentreIds={selectedCentreIds}
          selectedCandidateIds={selectedCandidateIds}
          dateRange={selectedDateRange}
          onKeywordChange={setSearchInput}
          onCentreIdsChange={setSelectedCentreIds}
          onCandidateIdsChange={setSelectedCandidateIds}
          onDateRangeChange={setSelectedDateRange}
          handleSortChange={handleSortChange}
        />
        <div className="d-flex flex-column">
          <TableHeader
            variant={isClient ? "client-pending" : "admin-pending"}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={handleSortChange}
          />
          {pendingTimesheets.length > 0 ? (
            pendingTimesheets.map((timesheet, index) => (
              <div key={index}>
                <TableRow
                  key={timesheet.id}
                  variant={isClient ? "client-pending" : "admin-pending"}
                  timesheet={timesheet}
                  btnType={timesheet?.is_declined_timesheet ? "View" : null}
                  onViewClick={() => handleViewTimesheet(timesheet)}
                />
              </div>
            ))
          ) : (
            <div className="d-flex gap-20 align-items-center justify-content-center text-center p-15px">
              <div className="fs-16 fw-400 text-grey-medium">
                No pending timesheets found
              </div>
            </div>
          )}

          {/* Render pagination controls if there are more than 10 users */}
          {pendingPagination &&
            pendingPagination?.total > DEFAULT_ITEMS_PER_PAGE && (
              <PaginationControls
                currentPage={page} // Current page number
                totalPages={pendingPagination?.last_page} // Total number of pages
                paginationLinks={pendingPagination?.links} // List of pagination links
                onPageChange={handlePageChange} // Function to handle page change
                setPerPageList={setPerPage} // Function to set the number of items per page
                setCurrentPage={setPage} // Function to set the current page
              />
            )}
        </div>
      </div>

      {/* View Timesheet Modal */}
      <PopupModal
        visible={showViewTimesheetModal}
        showCloseBtn={true}
        onClickClose={onClickClose}
        customWidth={650}
        popUpClass="mobile-model"
      >
        {selectedTimesheet && <ViewTimesheet timesheet={selectedTimesheet} timesheetType="pending"/>}
      </PopupModal>
    </div>
  );
};

export default TimesheetPending;
